import { useEffect, useRef } from "react";
import { useNavigationType } from "react-router-dom";

// El elemento con scroll es `.content_app` (ver LayoutStyles.scss), no `window`:
// `.layout_container` mide 100vh y el contenido vive dentro de un div con
// `overflow-y: auto`. Por eso el navegador no restaura la posición por sí solo
// y cualquier `window.scrollTo` sobre estas páginas es un no-op.
const SCROLL_CONTAINER = ".content_app";
const STORAGE_KEY = "scrollPositions";
// Se guardan las últimas N URLs para poder volver atrás por más de un listado
// sin que sessionStorage crezca sin límite.
const MAX_ENTRIES = 10;
// Reintentos de restauración mientras el contenido termina de tomar su altura
// final (banners, imágenes). Las cards miden 520px fijos, así que en la práctica
// alcanza con los primeros frames.
const MAX_RESTORE_FRAMES = 40;
// Frames seguidos en la posición correcta antes de dar la restauración por buena.
const SETTLED_FRAMES = 5;

type ScrollPositions = Record<string, number>;

const currentKey = (): string => `${window.location.pathname}${window.location.search}`;

const readPositions = (): ScrollPositions => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? (JSON.parse(raw) as ScrollPositions) : {};
    } catch {
        return {};
    }
};

const savePosition = (key: string, top: number): void => {
    try {
        const positions = readPositions();
        delete positions[key]; // re-insertar para que quede como la entrada más reciente
        positions[key] = top;
        const keys = Object.keys(positions);
        for (const staleKey of keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES))) {
            delete positions[staleKey];
        }
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
    } catch {
        // sessionStorage lleno o bloqueado: la restauración es un extra, no se rompe la página
    }
};

/**
 * Restaura la posición del scroll al volver (back/forward) a una página de listado.
 *
 * Las páginas de listado se remontan al volver desde el detalle, así que hay que
 * guardar la posición mientras el usuario navega y reaplicarla cuando los
 * resultados ya están en pantalla.
 *
 * La clave incluye pathname + search, de modo que los filtros y la página activa
 * (que se persisten con `history.replaceState`) identifican la posición guardada.
 *
 * @param isReady `true` cuando los resultados ya están renderizados y el contenedor
 *                tiene su altura final. Antes de eso el scroll se recortaría a 0.
 */
export const useScrollRestoration = (isReady: boolean): void => {
    const navigationType = useNavigationType();
    // Se lee en el primer render, antes de que el listener de scroll pueda pisarlo.
    const targetRef = useRef<number | undefined>(navigationType === "POP" ? readPositions()[currentKey()] : undefined);
    const restoredRef = useRef(false);
    const cancelledRef = useRef(false);

    // Guardar la posición mientras el usuario navega el listado.
    useEffect(() => {
        const container = document.querySelector(SCROLL_CONTAINER);
        if (!container) return;

        let frame = 0;
        const handleScroll = () => {
            if (frame) return; // un guardado por frame como máximo
            frame = requestAnimationFrame(() => {
                frame = 0;
                savePosition(currentKey(), container.scrollTop);
            });
        };
        // Si el usuario scrollea a mano mientras carga, no le movemos la página debajo.
        const cancelRestore = () => {
            if (!restoredRef.current) cancelledRef.current = true;
        };

        container.addEventListener("scroll", handleScroll, { passive: true });
        container.addEventListener("wheel", cancelRestore, { passive: true });
        container.addEventListener("touchmove", cancelRestore, { passive: true });

        return () => {
            container.removeEventListener("scroll", handleScroll);
            container.removeEventListener("wheel", cancelRestore);
            container.removeEventListener("touchmove", cancelRestore);
            if (frame) cancelAnimationFrame(frame);
        };
    }, []);

    // Reaplicar la posición una sola vez, cuando el listado ya está pintado.
    useEffect(() => {
        if (restoredRef.current || !isReady) return;
        restoredRef.current = true;

        const target = targetRef.current;
        if (!target || cancelledRef.current) return;

        const container = document.querySelector(SCROLL_CONTAINER);
        if (!container) return;

        let frames = 0;
        let settled = 0;
        const apply = () => {
            if (cancelledRef.current || frames++ >= MAX_RESTORE_FRAMES) return;
            // El navegador recorta scrollTop mientras el contenido no tenga altura
            // suficiente. Reaplicar también cubre el segundo `_handleSearch` que
            // disparan los listados al montar (vuelve a skeletons y luego a resultados).
            if (container.scrollTop < target - 2) {
                container.scrollTop = target;
                settled = 0;
            } else if (++settled >= SETTLED_FRAMES) {
                return; // posición alcanzada y estable
            }
            requestAnimationFrame(apply);
        };
        requestAnimationFrame(apply);
    }, [isReady]);
};

export default useScrollRestoration;
